import React, { useEffect, useRef, useState } from "react";
import tw from "tailwind-styled-components";
import { Formik, Field, Form } from "formik";
import { MdClose } from "react-icons/md";
import {
    FaCheckCircle,
    FaMoneyBillWave,
    FaExclamationCircle,
    FaRegCalendar,
} from "react-icons/fa";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    Summary,
    SummaryTitle,
    SummaryRow,
    SummaryLabel,
    SummaryValue,
    CalendarIcon,
    RupeeIcon,
} from "../Styles/InputStyles";
import { toast } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";
import Config from "../../Config";
import moment from "moment";

const MarkAsPaidOnlineModal = ({
    handleClose,
    markAsPaidMutate,
    markAsPaidLoading,
    selectedData,
    selectedExpense
}) => {

    const formikRef = useRef(null);
    const [height, setHeight] = useState(window.innerHeight);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Safe access to values with fallbacks
    const getExpenseId = () => {
        return selectedExpense?.id || selectedData?.id;
    };

    const getExpenseType = () => {
        return selectedExpense?.type || selectedData?.type || 2;
    };

    const getBalance = () => {
        return selectedExpense?.balance || selectedData?.balance || 0;
    };

    const initialValues = {
        amountPaid: getBalance(),
        remarks: selectedData?.description || "",
        paymentType: 0,
    };

    const paymentTypes = [
        {
            label: "Full Payment",
            value: 0,
            icon: <FaCheckCircle className="w-6 h-6 text-green-500" />,
        },
        {
            label: "Partial Payment",
            value: 1,
            icon: <FaMoneyBillWave className="w-6 h-6 text-blue-500" />,
        },
    ];

    const handleSubmit = (values, formikHelpers) => {

        // Validate required fields
        if (values.paymentType === undefined || values.paymentType === null) {
            toast.error("Payment type is required");
            return;
        }

        if (values.paymentType !== 0) {
            if (!values.amountPaid) {
                toast.error("Amount is required");
                return;
            }
            if (values.amountPaid <= 10) {
                toast.error("Amount must be more than Rs10");
                return;
            }
            if (values.amountPaid > getBalance()) {
                toast.error("Amount cannot exceed total dues for partial payment");
                return;
            }
        }

        const amountToSend = values.paymentType === 0 ? getBalance() : Number(values.amountPaid);

        // Prepare API payload with safe access
        const payload = {
            expenseId: getExpenseId(),
            landlordId: selectedData?.landlordId,
            propId: selectedData?.propId,
            amount: amountToSend,
            paymentType: values?.paymentType,
            remarks: values?.remarks || "",
            type: getExpenseType(),
        };

        // Validate required payload fields
        if (!payload.expenseId) {
            toast.error("Expense ID is missing");
            return;
        }
        if (!payload.landlordId) {
            toast.error("Landlord ID is missing");
            return;
        }
        if (!payload.propId) {
            toast.error("Property ID is missing");
            return;
        }

        markAsPaidMutate(payload);
    };

    return (
        <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Header>
                <h1 className="text-white text-lg font-medium">Pay Landlord Rent</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </Header>

            <ProfileSection className="flex justify-between">
                <UserInfo>
                    <span className="font-medium mb-0 text-lg">
                        {selectedData?.landlordName || "N/A"}
                    </span>
                    <span className="text-sm text-gray-600">#{selectedData?.landlordMobile || "N/A"}</span>
                </UserInfo>
                <div className="flex flex-col gap-0">
                    <span className='text-gray-500 text-sm'>{'Balance'}</span>
                    <span className='text-xl font-semibold text-gray-700'>{'₹' + getBalance()}</span>
                </div>
            </ProfileSection>

            {selectedData?.lastPaidOn && (
                <ProfileSection className="flex justify-between bg-blue-50 py-1.5 text-xs font-medium text-gray-600">
                    <div>Last Rent Paid On:</div>
                    <div>{moment(selectedData?.lastPaidOn).format("DD MMM YYYY")}</div>
                </ProfileSection>
            )}

            <div className="flex flex-col justify-between flex-1">
                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ setFieldValue, values, resetForm, handleSubmit }) => (
                        <Form onSubmit={handleSubmit} className="flex flex-col flex-1">
                            <ScrollableContent className="flex-1">
                                <Wrapper className="px-6">
                                    <InputGroup>
                                        <Label htmlFor="paymentType">Payment Type *</Label>
                                        <div className="grid grid-cols-3 gap-2">
                                            {paymentTypes.map((option) => (
                                                <OptionCard
                                                    key={option.value}
                                                    type="button"
                                                    isselected={(values.paymentType === option.value).toString()}
                                                    onClick={() => {
                                                        setFieldValue("paymentType", option.value);
                                                        if (option.value === 0) {
                                                            setFieldValue("amountPaid", getBalance());
                                                        } else {
                                                            setFieldValue("amountPaid", "");
                                                        }
                                                    }}
                                                >
                                                    <span className="text-sm">{option.label}</span>
                                                </OptionCard>
                                            ))}
                                        </div>
                                    </InputGroup>

                                    {(values.paymentType === 0 || values.paymentType === 1) && (
                                        <InputGroup>
                                            <Label htmlFor="amountPaid">
                                                {values.paymentType === 0 ? "Amount" : "Partial Amount"} *
                                            </Label>
                                            <FieldWrappers className="w-full">
                                                <RupeeIcon />
                                                <Field
                                                    name="amountPaid"
                                                    type="number"
                                                    disabled={values.paymentType === 0}
                                                    placeholder="Enter Amount"
                                                    className="w-full pl-5"
                                                />
                                            </FieldWrappers>
                                        </InputGroup>
                                    )}

                                    <InputGroup>
                                        <Label htmlFor="remarks">Remarks</Label>
                                        <FieldWrapper>
                                            <Field
                                                as={Textarea}
                                                id="remarks"
                                                name="remarks"
                                                rows={4}
                                                maxLength={500}
                                                placeholder="Write your remark here..."
                                            />
                                        </FieldWrapper>
                                    </InputGroup>

                                    <Summary>
                                        <SummaryTitle>Summary</SummaryTitle>
                                        <SummaryRow>
                                            <SummaryLabel>Amount selected</SummaryLabel>
                                            <SummaryValue>
                                                ₹{values.paymentType === 0
                                                    ? getBalance()
                                                    : values.amountPaid || 0}
                                            </SummaryValue>
                                        </SummaryRow>
                                        <SummaryRow>
                                            <SummaryLabel>Amount outstanding</SummaryLabel>
                                            <SummaryValue>
                                                ₹{values.paymentType === 0
                                                    ? 0
                                                    : values.amountPaid
                                                        ? getBalance() - values.amountPaid
                                                        : getBalance()}
                                            </SummaryValue>
                                        </SummaryRow>
                                    </Summary>
                                </Wrapper>
                            </ScrollableContent>
                            <ActionSection>
                                <SideCancelBtn
                                    type="button"
                                    onClick={() => {
                                        resetForm();
                                        handleClose();
                                    }}
                                >
                                    Cancel
                                </SideCancelBtn>
                                <SubmitBtn
                                    type="submit"
                                    disabled={markAsPaidLoading}
                                >
                                    {markAsPaidLoading ? "Loading..." : "Pay"}
                                </SubmitBtn>
                            </ActionSection>
                        </Form>
                    )}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`;
const FieldWrapper = tw.div`w-full`;
const OptionCard = tw.button`
  flex flex-col items-center justify-center p-3 rounded-lg shadow border border-gray-200 text-center
  cursor-pointer transition-all duration-200 
  ${(props) =>
        props.isselected === "true"
            ? "bg-blue-50 border-blue-500"
            : "bg-white hover:bg-gray-50"}
`;
const Textarea = tw.textarea`min-h-30 border border-gray-300 rounded pl-2 py-2 focus:ring-1 focus:ring-sky-600 focus:border-sky-600 focus:outline-none duration-200 w-full h-30 resize-y break-words overflow-hidden`;
const UserInfo = tw.div`flex flex-col`;
const ProfileSection = tw.div`flex items-center gap-3 bg-gray-100 px-4 p-4`;

export default MarkAsPaidOnlineModal;